// The split card's rules, kept pure so the node pool can test them
// (2026-10-04, docs/design/data-needed-split-accounts-mockup-v1.html).
//
// The card holds one choice per account. This module turns those choices
// into the button's label, the line under it, the After box, and the
// answers the decide route takes. The server re-checks every one of them;
// these rules only make the card say the truth before the click.

import type { CleanupSplitAccount } from "../../../shared/cleanup/fix";

/** One row's choice. `value` is the select's value. */
export interface SplitRowChoice {
  /** "" unanswered · "family:<id>" a listed family · "found" a family the
   *  person searched for · "new" its own new household · "leave". */
  value: string;
  found: { id: string; label: string } | null;
  zip: string;
  bring: boolean;
}

export function initialChoice(a: CleanupSplitAccount): SplitRowChoice {
  if (a.suggestion?.to === "family") {
    return { value: `family:${a.suggestion.householdId}`, found: null, zip: "", bring: false };
  }
  if (a.suggestion?.to === "new") {
    // Grant's default (2026-10-04 brief): when the person the account
    // names is already here, "its own new household" brings them, or the
    // OS would make a second copy of the same person.
    return { value: "new", found: null, zip: "", bring: a.person != null };
  }
  return { value: "", found: null, zip: "", bring: false };
}

export type RowState = "needs" | "stay" | "chosen";

export function rowState(c: SplitRowChoice): RowState {
  if (c.value === "" || (c.value === "found" && !c.found)) return "needs";
  if (c.value === "leave") return "stay";
  return "chosen";
}

/** A zip is optional; one that is typed must be five digits. */
export function zipOk(c: SplitRowChoice): boolean {
  return c.value !== "new" || c.zip.trim() === "" || /^\d{5}$/.test(c.zip.trim());
}

export function newHouseholdLabel(a: CleanupSplitAccount): string | null {
  if (a.newHouseholdName) return a.newHouseholdName;
  return a.person ? a.person.name : null;
}

/** Where the row's account goes, in words ("Robert & Maria Fernandez"). */
export function destinationLabel(a: CleanupSplitAccount, c: SplitRowChoice): string | null {
  if (c.value.startsWith("family:")) {
    const id = c.value.slice("family:".length);
    return a.families.find((f) => f.householdId === id)?.label ?? null;
  }
  if (c.value === "found") return c.found?.label ?? null;
  if (c.value === "new") {
    const name = newHouseholdLabel(a);
    return name ? `${name} (new household)` : "a new household";
  }
  return null;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export interface SplitSummary {
  open: number;
  move: number;
  stay: number;
  badZip: boolean;
  /** Two ticked accounts bringing one person to two places. */
  personConflict: string | null;
  canGo: boolean;
  label: string;
  fine: string;
  after: string[];
  leftLine: string;
}

export function summarize(
  accounts: readonly CleanupSplitAccount[],
  choices: Readonly<Record<string, SplitRowChoice>>,
): SplitSummary {
  let open = 0;
  let move = 0;
  let stay = 0;
  let badZip = false;
  const after: string[] = [];
  const personTo = new Map<string, string>();
  let personConflict: string | null = null;
  for (const a of accounts) {
    const c = choices[a.portfolioId] ?? initialChoice(a);
    const st = rowState(c);
    if (st === "needs") open += 1;
    else if (st === "stay") stay += 1;
    else {
      move += 1;
      if (!zipOk(c)) badZip = true;
      const dest = destinationLabel(a, c) ?? "a household";
      after.push(`${a.lastFour ? `····${a.lastFour}` : a.title} to ${dest}`);
      if (c.bring && a.person) {
        const key = c.value === "new" ? `new:${a.portfolioId}` : c.value === "found" ? `found:${c.found?.id}` : c.value;
        const prev = personTo.get(a.person.contactId);
        if (prev && (prev !== key || key.startsWith("new:"))) personConflict = a.person.name;
        personTo.set(a.person.contactId, key);
      }
    }
  }
  let label: string;
  let fine: string;
  if (open > 0) {
    label = `Pick a home for ${plural(open, "more account", "more accounts")}`;
    fine = accounts.length === 1 ? "Nothing moves until it has an answer." : "Nothing moves until every account has an answer.";
  } else if (move === 0) {
    label = "Nothing to move";
    fine = "Every account is set to stay. Pick one to move, or close with Later.";
  } else {
    label = `Move ${plural(move, "account", "accounts")}`;
    fine = "Moves each account as its own record. One Undo for 60 seconds puts every one back.";
  }
  if (badZip) fine = "A zip is five digits, or leave it blank.";
  if (personConflict) fine = `${personConflict} can only go to one place. Untick one of their accounts.`;
  const leftLine =
    open > 0
      ? `Waiting on ${plural(open, "more answer", "more answers")}.`
      : stay > 0
        ? `${stay} stay${stay === 1 ? "s" : ""} in the holding household. This item stays on your list until it is empty.`
        : "The empty holding household is archived and this item closes.";
  return {
    open,
    move,
    stay,
    badZip,
    personConflict,
    canGo: open === 0 && move > 0 && !badZip && !personConflict,
    label,
    fine,
    after,
    leftLine,
  };
}

/** The decide route's `params.accounts`, one entry per account. */
export function answersFor(
  accounts: readonly CleanupSplitAccount[],
  choices: Readonly<Record<string, SplitRowChoice>>,
): Record<string, unknown>[] {
  return accounts.map((a) => {
    const c = choices[a.portfolioId] ?? initialChoice(a);
    const bring = c.bring && a.person != null;
    if (c.value.startsWith("family:")) {
      return { portfolio_id: a.portfolioId, to: "family", household_id: c.value.slice(7), bring_person: bring };
    }
    if (c.value === "found" && c.found) {
      return { portfolio_id: a.portfolioId, to: "family", household_id: c.found.id, bring_person: bring };
    }
    if (c.value === "new") {
      const zip = c.zip.trim();
      return { portfolio_id: a.portfolioId, to: "new", ...(zip ? { zip } : {}), bring_person: bring };
    }
    return { portfolio_id: a.portfolioId, to: "leave" };
  });
}

export function dollarsWhole(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString("en-US")}`;
}
